import { useEffect, useMemo, useState } from "react";
import { ArtPanel } from "./components/ArtPanel";
import { getCompletedRoute, getSceneId, getScenePresentation } from "./story/engine";
import { loadGameData } from "./story/load";
import type {
  Answer,
  ArtManifest,
  HistoryEntry,
  Language,
  Story,
  UiContent,
} from "./story/types";

type Screen = "language" | "intro" | "decision" | "consequence" | "ending";
type GameData = { story: Story; ui: UiContent; art: ArtManifest };

const local = (value: Record<Language, string>, language: Language) => value[language];

export function App() {
  const [data, setData] = useState<GameData>();
  const [loadFailed, setLoadFailed] = useState(false);
  const [language, setLanguage] = useState<Language>("cz");
  const [screen, setScreen] = useState<Screen>("language");
  const [history, setHistory] = useState<HistoryEntry[]>([]);

  useEffect(() => {
    loadGameData().then(setData).catch((error: unknown) => {
      console.error(error);
      setLoadFailed(true);
    });
  }, []);

  useEffect(() => {
    document.documentElement.lang = language === "cz" ? "cs" : "en";
  }, [language]);

  const scene = useMemo(() => {
    if (!data || history.length >= 6) return undefined;
    return data.story.scenes[getSceneId(data.story, history)];
  }, [data, history]);

  if (!data) {
    return <main className="boot-state" aria-busy={!loadFailed} data-content-state={loadFailed ? "failed" : "loading"} />;
  }

  const { story, ui, art } = data;
  const reset = (nextScreen: Screen = "language") => {
    setHistory([]);
    setScreen(nextScreen);
  };
  const choose = (answer: Answer) => {
    if (!scene) return;
    const choice = scene.choices[answer];
    setHistory((current) => [
      ...current,
      { sceneId: scene.id, answer, momentId: choice.moment.id },
    ]);
    setScreen("consequence");
  };
  const continueAfterConsequence = () => {
    setScreen(history.length === 6 ? "ending" : "decision");
  };

  if (screen === "language") {
    return (
      <main className="language-screen page-frame">
        <div className="brand-mark" aria-hidden="true">6</div>
        <p className="eyebrow">{ui.appTitle.cz} · {ui.appTitle.en}</p>
        <h1>{ui.languagePrompt.cz}<span>{ui.languagePrompt.en}</span></h1>
        <div className="language-actions">
          {(["cz", "en"] as const).map((code) => (
            <button key={code} className="primary-button" onClick={() => { setLanguage(code); setScreen("intro"); }}>
              <b>{code.toUpperCase()}</b>{ui.languageNames[code]}
            </button>
          ))}
        </div>
      </main>
    );
  }

  const header = (
    <header className="game-header">
      <strong>{local(ui.appTitle, language)}</strong>
      <button className="text-button" onClick={() => reset("language")}>{local(ui.changeLanguage, language)}</button>
    </header>
  );

  if (screen === "intro") {
    return (
      <main className="page-frame">
        {header}
        <section className="comic-layout intro-layout">
          <ArtPanel artId={story.intro.artId} manifest={art} language={language} />
          <div className="story-panel">
            <p className="fiction-note">{local(ui.fictionNote, language)}</p>
            <h1>{local(story.intro.title, language)}</h1>
            <p>{local(story.intro.text, language)}</p>
            <button className="primary-button" onClick={() => setScreen("decision")}>{local(ui.start, language)}</button>
          </div>
        </section>
      </main>
    );
  }

  if (screen === "decision" && scene) {
    const presentation = getScenePresentation(scene, history);
    const progress = local(ui.decisionProgress, language)
      .replace("{current}", String(scene.decision))
      .replace("{total}", "6");
    return (
      <main className="page-frame">
        {header}
        <section className="comic-layout">
          <ArtPanel artId={presentation.artId} manifest={art} language={language} />
          <div className="story-panel">
            <p className="eyebrow">{progress}</p>
            <p className="situation">{local(presentation.situation, language)}</p>
            <h1>{local(scene.question, language)}</h1>
          </div>
        </section>
        <section className="choice-grid">
          {(["yes", "no"] as const).map((answer) => {
            const choice = scene.choices[answer];
            return (
              <button key={answer} className={`choice-card choice-card--${answer}`} onClick={(event) => {
                event.currentTarget.disabled = true;
                choose(answer);
              }}>
                <span className="choice-answer">{local(ui[answer], language)}</span>
                <span><b>{local(ui.benefitLabel, language)}</b>{local(choice.benefit, language)}</span>
                <span><b>{local(ui.riskLabel, language)}</b>{local(choice.risk, language)}</span>
              </button>
            );
          })}
        </section>
      </main>
    );
  }

  if (screen === "consequence") {
    const latest = history.at(-1);
    const playedScene = latest ? story.scenes[latest.sceneId] : undefined;
    const choice = latest && playedScene ? playedScene.choices[latest.answer] : undefined;
    if (!choice) return null;
    return (
      <main className="page-frame">
        {header}
        <section className="comic-layout consequence-layout">
          <ArtPanel artId={choice.consequence.artId} manifest={art} language={language} />
          <div className="story-panel">
            <p className="eyebrow">{local(ui.consequenceLabel, language)} · {local(ui[latest!.answer], language)}</p>
            <h1>{local(choice.consequence.text, language)}</h1>
            <button className="primary-button" onClick={(event) => {
              event.currentTarget.disabled = true;
              continueAfterConsequence();
            }}>{local(ui.continue, language)}</button>
          </div>
        </section>
      </main>
    );
  }

  const route = getCompletedRoute(story, history);
  const ending = story.endings[route.endingId];
  const moments = route.recapMomentIds.map((momentId) => {
    const entry = history.find(({ momentId: playedMomentId }) => playedMomentId === momentId)!;
    const choice = story.scenes[entry.sceneId].choices[entry.answer];
    return { ...choice.moment, artId: choice.consequence.artId };
  });
  return (
    <main className="page-frame">
      {header}
      <section className="comic-layout ending-layout">
        <ArtPanel artId={ending.artId} manifest={art} language={language} />
        <div className="story-panel">
          <h1>{local(ending.title, language)}</h1>
          <p>{local(route.endingBody ?? ending.body, language)}</p>
        </div>
      </section>
      <section className="ending-footer">
        <div className="ending-recap">
          <h2>{local(ui.endingMoments, language)}</h2>
          <ol className="moment-grid">
            {moments.map((moment) => (
              <li key={moment.id}>
                <div className="moment-art"><ArtPanel artId={moment.artId} manifest={art} language={language} /></div>
                <span>{local(moment.endingText, language)}</span>
              </li>
            ))}
          </ol>
        </div>
        <div className="ending-actions">
          <p className="fiction-note">{local(ui.fictionNote, language)}</p>
          <div>
            <button className="primary-button" onClick={() => reset("intro")}>{local(ui.restart, language)}</button>
            <button className="text-button" onClick={() => reset("language")}>{local(ui.changeLanguage, language)}</button>
          </div>
        </div>
      </section>
    </main>
  );
}
